import type { ReactNode } from "react";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { NavMain, type NavGroup } from "@/components/navigation/nav-main";
import { NavUser } from "@/components/navigation/nav-user";
import { OperatorMark } from "@/components/brand/OperatorMark";
import { cn } from "@/lib/cn";

interface SidebarProps {
  collapsed?: boolean;
  onToggle?: () => void;
  groups: NavGroup[];
  brand?: 'operator' | 'console';
  /** Optional slot rendered between the brand row and the nav. Used by the
   *  console layout to mount the space switcher. */
  belowBrand?: ReactNode;
}

export default function Sidebar({
  collapsed = false,
  onToggle,
  groups,
  brand = 'operator',
  belowBrand,
}: SidebarProps) {
  const wordmark = brand === 'console' ? 'console' : 'operator';

  return (
    <TooltipProvider>
      <div
        data-testid="sidebar"
        // `h-screen` (not `inset-y-0`) so the fixed sidebar respects the
        // keyboard-shrunken `--app-height` (see index.css) instead of
        // spanning the layout viewport under the on-screen keyboard.
        className="hidden h-screen lg:fixed lg:top-0 lg:z-50 lg:flex lg:flex-col transition-all duration-500 ease-in-out"
        style={{ width: collapsed ? '4rem' : '16rem' }}
      >
        <div className="flex min-h-0 flex-1 flex-col border-r border-border bg-background">
          {/* Logo + collapse toggle */}
          <div
            className={cn(
              "flex h-16 shrink-0 items-center",
              collapsed ? "justify-center px-2" : "px-4"
            )}
          >
            {collapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={onToggle}
                    aria-label="Expand sidebar"
                    className="group relative flex h-8 w-8 items-center justify-center rounded-md text-foreground hover:bg-accent transition-colors focus-ring"
                  >
                    <OperatorMark className="h-6 w-6 transition-opacity group-hover:opacity-0" />
                    <PanelLeftOpen className="absolute h-5 w-5 opacity-0 transition-opacity group-hover:opacity-100" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  Expand sidebar
                </TooltipContent>
              </Tooltip>
            ) : (
              <>
                <OperatorMark
                  className={cn(
                    "h-6 w-6",
                    brand === 'console' ? "text-muted-foreground" : "text-foreground",
                  )}
                />
                {/* Wordmark + a small "Preview" tag floated underneath. The
                    inner div keeps `overflow-hidden` so the wordmark still
                    clips cleanly during sidebar collapse animation, while
                    the outer relative wrapper lets the absolute-positioned
                    tag escape that clip and sit in the empty band below
                    the wordmark within the h-16 header row.
                    Left-aligned with a small left padding so it lines up
                    with the wordmark glyphs. Faded via `text-foreground/25`
                    so it reads as a quiet label, not a status banner. */}
                <div className="relative ml-2 flex-1">
                  <div className="overflow-hidden">
                    <span
                      className={cn(
                        "block font-wordmark text-xl font-extrabold tracking-[0.2em] whitespace-nowrap",
                        brand === 'console' ? "text-muted-foreground" : "text-foreground",
                      )}
                    >
                      {wordmark}
                    </span>
                  </div>
                  {brand === 'operator' && (
                    <span
                      aria-hidden
                      className="pointer-events-none absolute left-0 top-full -mt-1 pl-[4.1rem] text-[9px] font-semibold uppercase tracking-[0.25em] text-foreground/55"
                    >
                      Preview
                    </span>
                  )}
                </div>
                <button
                  onClick={onToggle}
                  aria-label="Collapse sidebar"
                  className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors focus-ring"
                >
                  <PanelLeftClose className="h-4 w-4" />
                </button>
              </>
            )}
          </div>

          {/* Optional slot below the brand row (e.g. console space switcher) */}
          {belowBrand && (
            <div className="shrink-0 pt-2">{belowBrand}</div>
          )}

          {/* Navigation */}
          <div className="no-scrollbar flex flex-1 flex-col overflow-y-auto pt-5 pb-4">
            <NavMain groups={groups} collapsed={collapsed} />
          </div>

          {/* User menu */}
          <div className="shrink-0 border-t border-border p-2">
            <NavUser collapsed={collapsed} />
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
